import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Card, Chip, IconButton, CardContent, Grid, Tooltip as TooltipCustom, useMediaQuery, Backdrop, CircularProgress, useTheme, Stack } from '@mui/material';
import { Menu as MenuIcon, Help as HelpIcon } from '@mui/icons-material';
// app UI controller for toggling sidenav
import { useMaterialUIController, setMiniSidenav } from "context";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import SouthIcon from '@mui/icons-material/South';
import FilterPanelContainer from "components/Filter/Filterpopup"
import KpiMetricscards from "components/KPI/KpiMetricscards";
import TimeSeriescards from "components/TimeSeries/TimeSeriescards";
import HighlightsCards from "components/Highlights/Highlightscard";
import Visualizations from "components/Visualizations/Visualizations"
import PieLegendCard from "components/Visualizations/Piecharts"
import Barcharts from "components/Visualizations/Barcharts"
import LineChartSubBrand from "components/Visualizations/Linechartsubbrand";
import LineChartsale from "components/Visualizations/LineChartsale"
import InventoryCharts from "components/Visualizations/Inventorycharts";
import WaterfallChart from "components/Visualizations/waterfallchart";
import TopSkuBarChart from "components/Visualizations/SkuBarchart";
import SkuSingleBarChart from "components/Visualizations/SingleBarchartSku";
import datadrilldown from "assets/images/selectedicons/DataDrillDown.svg";
import drillDownArrow from "assets/images/selectedicons/DrillDownArrow.svg";
import FilterChip from "components/FilterChip/FilterChip";

const TechnologyDashboard = ({ subcategoryToken: propSubcategoryToken }) => {
    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav } = controller;
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Below 900px
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Between 900px and 1200px

    // State for API data
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const header = document.querySelector('.MuiAppBar-root');
        if (header) {
            header.style.display = 'none';
        }
    }, []);

    // Toggle sidenav from the page menu icon
    const isTogglingSidenavRef = useRef(false);
    const handleToggleSidenav = useCallback(() => {
        if (isTogglingSidenavRef.current) return;
        isTogglingSidenavRef.current = true;
        // Defer to next frame to avoid blocking heavy charts paint
        requestAnimationFrame(() => {
            setMiniSidenav(dispatch, !miniSidenav);
            setTimeout(() => {
                isTogglingSidenavRef.current = false;
            }, 150);
        });
    }, [dispatch, miniSidenav]);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
            } catch (error) {
                console.error('Failed loading static mocks or transforming data:', error);
                setError('Failed to load static mock data. Check competitive_analysis/data/allMocks.json');
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, [location.search, propSubcategoryToken]);

    // KPI cards data
    const Kpiscardsdata = [
        {
            title: "Service Ticket Count",
            values: [
                { label: "MTD", change: "0.6%", value: "182", status: "increase" },
                { label: "QTD", change: "2.3%", value: "552", status: "increase" },
                { label: "YTD", change: "-1.3%", value: "2,210", status: "decrease" }
            ]
        },
        {
            title: "Mean Time To Resolve (MTTR)",
            values: [
                { label: "MTD", change: "0.5%", value: "6.4 hrs", status: "increase" },
                { label: "QTD", change: "0.7%", value: "6.2 hrs", status: "increase" },
                { label: "YTD", change: "0.2%", value: "6.8 hrs", status: "increase" }
            ]
        },
        {
            title: "System Uptime",
            values: [
                { label: "MTD", change: "0.1%", value: "99.8%", status: "increase" },
                { label: "QTD", change: "0.1%", value: "99.9%", status: "increase" },
                { label: "YTD", change: "0.2%", value: "99.6%", status: "increase" }
            ]
        },
        {
            title: "Cyber Security Incidents",
            values: [
                { label: "MTD", change: "0.3%", value: "3", status: "increase" },
                { label: "QTD", change: "1.2%", value: "7", status: "increase" },
                { label: "YTD", change: "-0.9%", value: "14", status: "decrease" }
            ]
        },
        {
            title: "Average Response Time",
            values: [
                { label: "MTD", change: "0.6%", value: "17 min", status: "increase" },
                { label: "QTD", change: "1.5%", value: "16 min", status: "increase" },
                { label: "YTD", change: "-2.3%", value: "18 min", status: "decrease" }
            ]
        },
        {
            title: "First Contact Resolution (FCR)",
            values: [
                { label: "MTD", change: "0.2%", value: "78.4%", status: "increase" },
                { label: "QTD", change: "0.6%", value: "79.3%", status: "increase" },
                { label: "YTD", change: "0.3%", value: "75.2%", status: "increase" }
            ]
        },
        {
            title: "SLA Compliance Rate",
            values: [
                { label: "MTD", change: "0.4%", value: "94.2%", status: "increase" },
                { label: "QTD", change: "0.6%", value: "95.1%", status: "increase" },
                { label: "YTD", change: "0.7%", value: "90.3%", status: "increase" }
            ]
        },
        {
            title: "Urgent Priority Tickets",
            values: [
                { label: "MTD", change: "0.2%", value: "12", status: "increase" },
                { label: "QTD", change: "0.9%", value: "37", status: "increase" },
                { label: "YTD", change: "-1.7%", value: "15", status: "decrease" }
            ]
        },
        {
            title: "Average Tickets per Day",
            values: [
                { label: "MTD", change: "0.3%", value: "6.1", status: "increase" },
                { label: "QTD", change: "0.7%", value: "6.0", status: "increase" },
                { label: "YTD", change: "-0.4%", value: "5.8", status: "decrease" }
            ]
        }
    ];


    //second section cards
    const timeSeriesCardsdata = [
        {
            title: "Service Ticket Count",
            values: [
                { label: "Nov 2024", change: "+1.7%", value: "178", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+0.4%", value: "182", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+0.4%", value: "181", status: "increase", arrow: "left" }
            ]
        },

        {
            title: "Mean Time To Resolve",
            values: [
                { label: "Nov 2024", change: "+0.9%", value: "6.34 hrs", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+1.2%", value: "6.4 hrs", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+1.2%", value: "6.32 hrs", status: "increase", arrow: "left" }
            ]
        },

        {
            title: "System Uptime",
            values: [
                { label: "Nov 2024", change: "+0.2%", value: "99.6%", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+0.1%", value: "99.8%", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+0.1%", value: "99.7%", status: "increase", arrow: "left" }
            ]
        },

        {
            title: "Cyber Security Incidents",
            values: [
                { label: "Nov 2024", change: "-25%", value: "4", status: "decrease", arrow: "right" },
                { label: "Nov 2025", change: "-40%", value: "3", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "-40%", value: "5", status: "decrease", arrow: "left" }
            ]
        },

        {
            title: "Average Response Time",
            values: [
                { label: "Nov 2024", change: "-1.2%", value: "17.2 min", status: "decrease", arrow: "right" },
                { label: "Nov 2025", change: "+0.7%", value: "17 min", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+0.7%", value: "16.9 min", status: "increase", arrow: "left" }
            ]
        },

        {
            title: "First Contact Resolution (FCR)",
            values: [
                { label: "Nov 2024", change: "+0.7%", value: "77.7%", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+0.5%", value: "78.4%", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+0.5%", value: "77.9%", status: "increase", arrow: "left" }
            ]
        },

        {
            title: "SLA Compliance Rate",
            values: [
                { label: "Nov 2024", change: "+1.6%", value: "92.6%", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+1.3%", value: "94.2%", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+1.3%", value: "92.9%", status: "increase", arrow: "left" }
            ]
        },

        {
            title: "Urgent Priority Tickets",
            values: [
                { label: "Nov 2024", change: "-3.2%", value: "12.4", status: "decrease", arrow: "right" },
                { label: "Nov 2025", change: "+1.1%", value: "12", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+1.1%", value: "11.9", status: "increase", arrow: "left" }
            ]
        },

        {
            title: "Average Tickets per Day",
            values: [
                { label: "Nov 2024", change: "+2.2%", value: "6.0", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+0.6%", value: "6.1", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+0.6%", value: "6.06", status: "increase", arrow: "left" }
            ]
        }
    ];




    const [openFilters, setOpenFilters] = useState(false);
    const handleToggleFilters = () => {
        setOpenFilters((prev) => !prev);
    };

    // Filter state
    const [activeFilter, setActiveFilter] = useState('All');
    const [filterLoading, setFilterLoading] = useState(false);

    // Define filter presets for each quick filter
    const getFilterPreset = useCallback((filterName) => {
        const allCompanies = ["Nutrabolt", "Bloom"];
        const allCategories = ["Functional Beverage", "Active Nutrition"];
        const allBrands = ["C4", "Cellucor", "XTEND", "Bloom"];
        const allSubBrands = ["C4", "C4 Performance", "C4 Smart Energy", "C4 Ultimate", "XTEND", "Bloom Sparkling Energy", "Bloom Pop"];
        const allRoutes = ["Ecommerce", "Direct", "DSD"];
        const allChannels = ["Ecommerce", "Convenience Chain", "Convenience Independent", "Drug", "Food", "Multi Outlet", "Mass"];

        switch (filterName) {
            case 'All':
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Corporate':
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Systems':
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Custom Apps':
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            default:
                return null;
        }
    }, []);

    const [filterPresets, setFilterPresets] = useState(() => getFilterPreset('All'));

    // Handle filter click with full-page loader
    const handleFilterClick = (filter) => {
        if (filter === activeFilter || filterLoading) return;

        setFilterLoading(true);
        setLoading(true); // Show full-page loader

        // Simulate loading delay
        setTimeout(() => {
            const preset = getFilterPreset(filter);
            setActiveFilter(filter);

            // Set the preset - this will trigger FilterPanelContainer to apply it
            if (preset) {
                setFilterPresets(preset);
            } else {
                setFilterPresets(null);
            }

            setFilterLoading(false);
            setLoading(false); // Hide full-page loader

            // Open filter panel for "Custom" filter
            if (filter === 'Custom') {
                setOpenFilters(true);
            }
        }, 800);
    };

    // Handle filter changes from FilterPanelContainer
    const handleFiltersChange = useCallback((newFilters) => {
        // Don't check for preset match if we're already in Custom mode
        // FilterPanelContainer's isApplyingPresetRef prevents callback when applying presets
        if (activeFilter === 'Custom') return;

        // Check if the current filters match any preset
        const presetNames = ['All', 'Corporate', 'Systems', 'Custom Apps'];
        let matchedPreset = null;

        for (const presetName of presetNames) {
            const preset = getFilterPreset(presetName);
            if (preset) {
                const isMatching = Object.keys(preset).every(key => {
                    const presetValues = [...(preset[key] || [])].sort();
                    const currentValues = [...(newFilters[key] || [])].sort();
                    return presetValues.length === currentValues.length &&
                        presetValues.every((val, idx) => val === currentValues[idx]);
                });

                if (isMatching) {
                    matchedPreset = presetName;
                    break;
                }
            }
        }

        // If filters don't match any preset, switch to Custom
        if (!matchedPreset && activeFilter !== 'Custom') {
            setActiveFilter('Custom');
            setFilterPresets(null);
        }
    }, [activeFilter, getFilterPreset]);

    const [activeIdx, setActiveIdx] = React.useState(0);
    const kpiBriefData = [
        {
            title: "Month",
            value: "$ 65.2 M",
            lyValue: "59.12 M",
            pct: "5.8%",
            positive: true,
            status: "increase",
            glvalue: "$ 85.5 M"
        },
        {
            title: "Quarter",
            value: "$ 180.6 M",
            lyValue: "160.29 M",
            pct: "11.9%",
            positive: true,
            status: "increase",
            glvalue: "$ 207.3 M"
        },
        {
            title: "Year",
            value: "$ 859.1 M",
            lyValue: "823.03 M",
            pct: "5.6%",
            positive: true,
            status: "increase",
            glvalue: "$ 882.8 M"
        }
    ];

    const ticketStatus = [
        { label: "Escalated", value: 24, color: "#D93025" },
        { label: "Resolved", value: 26, color: "#34A853" },
        { label: "Open", value: 12, color: "#1A73E8" },
        { label: "Pending", value: 40, color: "#F6C344" },
    ];
    const ticketPriority = [
        { label: "High", value: 30, color: "#EB1B1B" },
        { label: "Urgent", value: 13, color: "#000000" },
        { label: "Low", value: 22, color: "#1FA5EC" },
        { label: "Medium", value: 35, color: "#FE9423" },
    ];
    const ticketImpact = [
        { label: "Business Intelligence", value: 16, color: "#F6C344" },
        { label: "Integrations", value: 10, color: "#34A853" },
        { label: "Other", value: 5, color: "#000000" },
        { label: "Custom Apps", value: 35, color: "#1A73E8" },
        { label: "Dynamics 365", value: 34, color: "#5C2D91" },


    ];
    const ticketClassification = [
        { label: "Request", value: 18, color: "#1A73E8" },
        { label: "Question", value: 5, color: "#F6C344" },
        { label: "Task", value: 14, color: "#5F6368" },
        { label: "Enhancement", value: 8, color: "#34A853" },
        { label: "Problem", value: 13, color: "#B80672" },
        { label: "Incident", value: 25, color: "#D93025" },
        { label: "Project", value: 19, color: "#5C2D91" }
    ];





    const ticketCountByMonth = {
        title: "Ticket Count by Month",

        xLabels: [
            "Dec. 2025", "Jan. 2025", "Feb. 2025", "Mar. 2025",
            "Apr. 2025", "May. 2025", "Jun. 2025", "Jul. 2025",
            "Aug. 2025", "Sep. 2025", "Oct. 2025", "Nov. 2025"
        ],

        series: [
            { label: "Custom Apps", color: "#1A73E8" },
            { label: "Dynamics 365", color: "#5C2D91" },
            { label: "Business Intelligence", color: "#F6C344" },
            { label: "Integrations", color: "#34A853" },
            { label: "Other", color: "#000000" }
        ],

        dataMatrix: [
            // Custom, Dynamics, BI, Integrations, Other
            [17, 6, 2, 4, 3],  // Dec → total 32
            [14, 9, 2, 7, 3],  // Jan → total 35
            [5, 16, 1, 6, 3],  // Feb → total 31
            [13, 6, 2, 6, 4],  // Mar → total 31
            [22, 3, 2, 4, 2],  // Apr → total 33
            [22, 4, 2, 4, 4],  // May → total 36
            [17, 5, 2, 5, 2],  // Jun → total 31
            [12, 9, 2, 5, 6],  // Jul → total 34
            [10, 11, 2, 5, 2], // Aug → total 30
            [8, 14, 2, 7, 9],  // Sep → total 40
            [7, 17, 3, 7, 7],  // Oct → total 41
            [6, 22, 3, 8, 3]   // Nov → total 42
        ]
    };

    const ticketCountCustomApps = {
        "title": "Ticket Count by Top 10 Custom Apps",
        "yLabels": [
            "3PL Portal",
            "B2B Portal",
            "BevApp",
            "BillBack Manager",
            "BOM App",
            "CTG/TPM",
            "GNC Portal",
            "Operations Portal",
            "LABS",
            "Partners"
        ],
        "series": [
            { "label": "Low", "color": "#29A3F0" },
            { "label": "Medium", "color": "#FF9F2D" },
            { "label": "High", "color": "#E31A1C" },
            { "label": "Urgent", "color": "#000000" }
        ],
        "dataMatrix": [
            [4, 3, 10, 9],   // 3PL Portal → 26
            [12, 6, 0, 3],   // B2B Portal → 21
            [7, 7, 5, 0],    // BevApp → 19
            [5, 6, 3, 3],    // BillBack Manager → 17
            [4, 4, 5, 3],    // BOM App → 16
            [4, 5, 3, 2],    // CTG/TPM → 14
            [4, 4, 3, 1],    // GNC Portal → 12
            [5, 4, 1, 1],    // Operations Portal → 11
            [3, 3, 1, 2],    // LABS → 9
            [3, 2, 1, 1]     // Partners → 7
        ]
    }
    const ticketCountTopSubmitters = {
        "title": "Ticket Count by Top 10 Submitters",
        "yLabels": [
            "Marcus Keaton",
            "Jordan Hale",
            "Ava Linwood",
            "Collin Mercer",
            "Luke Arden",
            "Elise Marlow",
            "Mara Kincaid",
            "Dana Whitlock",
            "Reid Fallon",
            "Tessa Rowan"
        ],
        "series": [
            { "label": "Low", "color": "#29A3F0" },
            { "label": "Medium", "color": "#FF9F2D" },
            { "label": "High", "color": "#E31A1C" },
            { "label": "Urgent", "color": "#000000" }
        ],
        "dataMatrix": [
            [6, 4, 9, 8],   // Marcus Keaton → 27
            [12, 7, 5, 2],  // Jordan Hale → 26
            [8, 8, 6, 1],   // Ava Linwood → 23
            [5, 7, 4, 5],   // Collin Mercer → 21
            [4, 4, 6, 5],   // Luke Arden → 19
            [5, 6, 3, 4],   // Elise Marlow → 18
            [4, 5, 5, 3],   // Mara Kincaid → 17
            [4, 5, 3, 4],   // Dana Whitlock → 16
            [4, 4, 3, 2],   // Reid Fallon → 13
            [3, 3, 4, 1]    // Tessa Rowan → 11
        ]
    }



    return (

       
            <Box sx={{
                ml: 0,
                transition: "margin-left 0.25s ease",
                "& .MuiBox-root": { backgroundImage: "none" }
            }} style={{
               
                padding: isMobile ? '8px' : '0px 16px 0px 16px',
                fontFamily: 'inter !important'
            }}>
                {/* Loading Backdrop */}
                <Backdrop
                    open={loading}
                    sx={{
                        position: 'fixed',
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    }}
                >
                    <CircularProgress color="primary" />
                </Backdrop>

                <Box
                    sx={{
                        height: {
                            xs: "calc(100dvh - 140px)", // mobile header
                            md: "calc(100vh - 64px)",  // desktop header
                        },
                        overflowY: "auto",
                        // px: isMobile ? 1 : 2,
                        pb: "100px",
                        /* smooth scrolling */
                        scrollBehavior: "smooth",
                        WebkitOverflowScrolling: "touch", // iOS momentum

                        /* hide scrollbar */
                        scrollbarWidth: "none",          // Firefox
                        msOverflowStyle: "none",         // IE / Edge
                        "&::-webkit-scrollbar": {
                            display: "none",               // Chrome / Safari
                        },
                    }}
                >

                    {/* Section Title: KPI's */}
                    <Typography sx={{ fontSize: '16px', mb: 1, mt: 3 }}>
                        <span style={{ color: '#1672B980', fontWeight: 200, fontSize: "16px" }}>Technology / </span><span style={{ fontWeight: 200, fontSize: "16px" }}>KPI's</span>
                    </Typography>
                    <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                    <KpiMetricscards cardsData={Kpiscardsdata} kpiBriefData={kpiBriefData} />

                    {/* Section Title: Time Series */}
                    <Typography sx={{ fontSize: '16px', mb: 1, mt: 3 }}>
                        <span style={{ color: '#1672B980', fontWeight: 200, fontSize: "16px" }}>Technology / </span><span style={{ fontWeight: 200, fontSize: "16px" }}>Time Series</span>
                    </Typography>
                    <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                    <TimeSeriescards timeSeriesCards={timeSeriesCardsdata} />

                    <Box sx={{ mt: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ fontSize: '16px' }}>
                                <span style={{ color: "#1672B980", fontWeight: 200, fontSize: "16px" }}>Technology / </span> <span style={{ fontSize: "16px", fontWeight: 200 }}>Visualizations</span>
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {["YTD", "QTD", "MTD"].map((label, idx) => (
                                    <FilterChip
                                        key={label}
                                        label={label}
                                        idx={idx}
                                        activeIdx={activeIdx}
                                        onClick={setActiveIdx}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                    </Box>
                    <Grid
                        container
                        rowSpacing={2}
                        columnSpacing={2}
                        sx={{
                            mt: "26px !important",
                            alignItems: "stretch"   // keeps all cards same height
                        }}
                    >
                        <Grid item xs={12} sm={6} md={3} sx={{
                            pt: { xs: "0px !important", sm: "0px !important", md: "0px !important" }
                        }}   >
                            <PieLegendCard title="Active Tickets by Status" data={ticketStatus} />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} sx={{
                            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                        }}>
                            <PieLegendCard title="Ticket Volume by Priority" data={ticketPriority} />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} sx={{
                            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                        }}>
                            <PieLegendCard title="Ticket Volume by Impact Category" data={ticketImpact} />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} sx={{
                            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                        }}>
                            <PieLegendCard title="Ticket Volume by Classification" data={ticketClassification} />
                        </Grid>
                    </Grid>

                    <Grid container spacing={3} sx={{ mt: '34px !important' }}>
                        {/* LEFT CARD - USA MAP */}
                        <Grid item xs={12} md={6} sx={{
                            pt: { xs: "0px !important", sm: "0px !important", }
                        }}>
                            <SkuSingleBarChart
                                title={ticketCountCustomApps.title}
                                yLabels={ticketCountCustomApps.yLabels}
                                series={ticketCountCustomApps.series}
                                dataMatrix={ticketCountCustomApps.dataMatrix}
                                xTicks={[0, 5, 10, 15, 20, 25, 30]}
                                showUnit={false}
                            />

                        </Grid>

                        {/* RIGHT CARD - BAR CHART */}
                        <Grid item xs={12} md={6} sx={{
                            pt: { xs: "34px !important", sm: "34px !important", md: '0px !important' }
                        }}>
                            <SkuSingleBarChart
                                title={ticketCountTopSubmitters.title}
                                yLabels={ticketCountTopSubmitters.yLabels}
                                series={ticketCountTopSubmitters.series}
                                dataMatrix={ticketCountTopSubmitters.dataMatrix}
                                xTicks={[0, 5, 10, 15, 20, 25, 30]}
                                showUnit={false}
                            />
                        </Grid>
                    </Grid>

                    <Grid container sx={{ mt: '34px !important' }}>
                        <Grid item xs={12}>
                            <Barcharts
                                title={ticketCountByMonth.title}
                                xLabels={ticketCountByMonth.xLabels}
                                series={ticketCountByMonth.series}
                                dataMatrix={ticketCountByMonth.dataMatrix}
                                yTicks={[0, 10, 20, 30, 40, 50]}
                                rounded={false}
                                yAxisLabel="Count"
                                valueFormatter={(v) => `${v}`}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        
    );
};

export default TechnologyDashboard;